/**
 * Auto Q&A — Automation anlegen und ändern (Paket 19, Abschnitt D).
 *
 * Eigene Route, kein Dialog: eine Schrittfolge mit Fortsetzungsbedingungen
 * passt nicht in ein Modal. Dieselbe Maske für „neu" und „ändern" — zwei
 * Masken für dasselbe Formular driften auseinander, und die eine ist dann
 * immer die schlechtere.
 *
 * Paket 24 schließt D2: Anlass, typisierter Bedingungsausdruck und Schritte
 * werden in derselben Maske gepflegt. Die Ausdruckskomponente bearbeitet direkt
 * das Modell, das der Server beim Anlass auswertet.
 *
 * ── Der Vorschausatz ──────────────────────────────────────────────────────
 *
 * Er wird aus `vorschauSatz` in `@cubic/shared` gebildet, nicht hier
 * zusammengebaut: Derselbe Satz muss im Test entstehen können, sonst ist die
 * wichtigste Prüfung der Maske genau die eine, die nichts prüft.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BERUHIGUNG_SEKUNDEN_VORGABE,
  BUDGET_MARKEN_VORGABE,
  FORTSETZEN_WENN,
  QA_ANLASS_DEFINITIONEN,
  QA_ANLASS_GRUPPEN,
  QA_ZIELART_LABEL,
  QA_SCHRITTE_HOECHSTENS,
  leereQaBedingungsgruppe,
  pruefeQaBedingungen,
  zielartenFuerQaAnlass,
  vorschauSatz,
  type FortsetzenWenn,
  type QaAnlass,
  type QaAnlassGruppe,
  type QaBedingungsgruppe,
  type QaZeitplan,
} from "@cubic/shared";
import { Titel } from "@/components/entwurf/Titel";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Zustandsmarke } from "@/components/entwurf/Zustandsmarke";
import {
  RadioCardGroup,
  RadioCardIndicator,
  RadioCardItem,
} from "@/components/RadioCardGroup";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Input } from "@/components/Input";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { api, grundVon, istNetzfehler } from "@/lib/api";
import { meldungZu, type Meldung } from "@/lib/meldung";
import { useProjekte } from "@/lib/haken";
import { cx, focusRing } from "@/lib/utils";
import { AutoQaBedingungsEditor } from "./AutoQaBedingungsEditor";

/** Je Fortsetzungsbedingung ein erklärender Satz — D verlangt ihn (D3). */
const FORTSETZEN_TEXT: Readonly<Record<FortsetzenWenn, string>> = {
  immer: "Läuft in jedem Fall, auch nachdem der vorige Schritt scheiterte.",
  ohne_aufhalter:
    "Läuft nur, wenn der vorige Schritt keinen blockierenden Befund hinterließ.",
  ohne_fehler:
    "Läuft nur, wenn der vorige Schritt nicht scheiterte. Die Vorgabe.",
  nur_bei_aufhalter:
    "Läuft nur, wenn der vorige Schritt etwas gefunden hat — für Nachprüfungen.",
};

const FORTSETZEN_LABEL: Readonly<Record<FortsetzenWenn, string>> = {
  immer: "Immer",
  ohne_aufhalter: "Wenn nichts aufhält",
  ohne_fehler: "Wenn nichts scheiterte",
  nur_bei_aufhalter: "Nur wenn etwas aufhält",
};

type SchrittEntwurf = {
  reihenfolge: number;
  rezeptId: string | null;
  rezeptFassung: number | null;
  fortsetzenWenn: FortsetzenWenn;
};

const QUELL_LABEL: Readonly<Record<string, string>> = {
  gitlab: "GitLab",
  fachereignis: "Cubic",
  qualitaet: "Qualität",
  metarr: "Metarr",
  zeit: "Zeit",
  system: "System",
  manuell: "Manuell",
};

const STANDARD_ZEITPLAN: QaZeitplan = {
  intervall: "taeglich",
  minute: 0,
  stunde: 9,
  wochentag: 1,
  zeitzone:
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin",
};

function AnlassKatalogAuswahl({
  wert,
  onChange,
}: {
  wert: QaAnlass;
  onChange: (anlass: QaAnlass) => void;
}) {
  const [suche, setzeSuche] = useState("");
  const begriff = suche.trim().toLocaleLowerCase("de");
  const gruppiert = useMemo(
    () =>
      QA_ANLASS_GRUPPEN.map((gruppe) => ({
        gruppe,
        eintraege: QA_ANLASS_DEFINITIONEN.filter(
          (definition) =>
            definition.gruppe === gruppe &&
            (!begriff ||
              definition.label.toLocaleLowerCase("de").includes(begriff) ||
              definition.schluessel.toLocaleLowerCase("de").includes(begriff) ||
              definition.beschreibung.toLocaleLowerCase("de").includes(begriff)),
        ),
      })).filter((gruppe) => gruppe.eintraege.length > 0),
    [begriff],
  );
  const ausgewaehlt = QA_ANLASS_DEFINITIONEN.find(
    (definition) => definition.schluessel === wert,
  );
  const [offeneGruppe, setzeOffeneGruppe] = useState<QaAnlassGruppe>(
    ausgewaehlt?.gruppe ?? QA_ANLASS_GRUPPEN[0],
  );

  useEffect(() => {
    if (!begriff && ausgewaehlt) setzeOffeneGruppe(ausgewaehlt.gruppe);
  }, [begriff, ausgewaehlt]);

  return (
    <div className="max-w-[760px] border border-linie bg-flaeche">
      <div className="border-b border-linie p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-medium text-tinte1">{ausgewaehlt?.label}</div>
            <div className="mt-1 text-[12px] text-tinte4">
              {ausgewaehlt ? QUELL_LABEL[ausgewaehlt.quelle] : "Anlass"}
              {ausgewaehlt?.zielArten.length
                ? ` · ${ausgewaehlt.zielArten
                    .slice(0, 4)
                    .map((art) => QA_ZIELART_LABEL[art])
                    .join(", ")}${ausgewaehlt.zielArten.length > 4 ? " …" : ""}`
                : ""}
            </div>
          </div>
          <span className="font-mono text-[11px] text-tinte4">
            {QA_ANLASS_DEFINITIONEN.length} Anlässe
          </span>
        </div>
        <Input
          value={suche}
          onChange={(event) => setzeSuche(event.target.value)}
          placeholder="Anlass nach Name oder Schlüssel durchsuchen …"
          aria-label="Anlässe durchsuchen"
          className="mt-4 w-full"
        />
      </div>
      <div
        className="max-h-[420px] overflow-y-auto overscroll-contain p-2"
        role="listbox"
        aria-label="Anlasskatalog"
      >
        {gruppiert.map(({ gruppe, eintraege }) => (
          <div key={gruppe} className="mb-3 last:mb-0">
            <button
              type="button"
              aria-expanded={Boolean(begriff) || offeneGruppe === gruppe}
              onClick={() => setzeOffeneGruppe(gruppe)}
              className={cx(
                "sticky top-0 z-10 flex w-full items-center justify-between bg-flaeche/95 px-2 py-2 text-left backdrop-blur-sm",
                focusRing,
              )}
            >
              <span className="flex items-center gap-2">
                <span aria-hidden="true" className="font-mono text-[11px] text-tinte4">
                  {Boolean(begriff) || offeneGruppe === gruppe ? "−" : "+"}
                </span>
                <Mikrolabel grad={11.5}>{gruppe}</Mikrolabel>
              </span>
              <span className="font-mono text-[10px] text-tinte4">
                {eintraege.length}
              </span>
            </button>
            {(Boolean(begriff) || offeneGruppe === gruppe) &&
              eintraege.map((definition) => {
              const aktiv = definition.schluessel === wert;
              return (
                <button
                  key={definition.schluessel}
                  type="button"
                  role="option"
                  aria-selected={aktiv}
                  onClick={() => onChange(definition.schluessel)}
                  className={cx(
                    "group flex w-full items-start gap-3 border-l-2 px-3 py-2.5 text-left transition-colors",
                    aktiv
                      ? "border-akzent bg-akzent/8"
                      : "border-transparent hover:bg-flaeche2",
                    focusRing,
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cx(
                      "mt-1 size-2 flex-none rounded-full border",
                      aktiv ? "border-akzent bg-akzent" : "border-tinte4",
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block text-[13.5px] text-tinte1">
                      {definition.label}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-[10.5px] text-tinte4">
                      {definition.schluessel}
                    </span>
                  </span>
                  <span className="ml-auto flex-none text-[10.5px] text-tinte4">
                    {QUELL_LABEL[definition.quelle]}
                  </span>
                </button>
              );
              })}
          </div>
        ))}
        {gruppiert.length === 0 ? (
          <div className="px-3 py-10 text-center text-[13px] text-tinte4">
            Kein Anlass passt zu „{suche}“.
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function AutoQaAutomationDetail() {
  const { automationId } = useParams({
    from: "/_app/auto-qa/automationen/$automationId",
  });
  const istNeu = automationId === "neu";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projekte = useProjekte();

  const rezepte = useQuery({
    queryKey: ["cubic", "durchsicht", "rezepte"],
    queryFn: () => api.durchsicht.rezepte.query(),
  });

  const bestand = useQuery({
    queryKey: ["cubic", "auto-qa", "automation", automationId],
    queryFn: () => api.autoQa.automation.query({ id: automationId }),
    enabled: !istNeu,
  });

  const [projektId, setzeProjektId] = useState("");
  const [name, setzeName] = useState("");
  const [anlass, setzeAnlass] = useState<QaAnlass>("mr_eroeffnet");
  const [bedingungen, setzeBedingungen] = useState<QaBedingungsgruppe>(() =>
    leereQaBedingungsgruppe(),
  );
  const [zustand, setzeZustand] = useState<"entwurf" | "aktiv" | "pausiert">(
    "entwurf",
  );
  const [beruhigung, setzeBeruhigung] = useState(BERUHIGUNG_SEKUNDEN_VORGABE);
  const [budget, setzeBudget] = useState<number | null>(BUDGET_MARKEN_VORGABE);
  const [zeitplan, setzeZeitplan] = useState<QaZeitplan>(STANDARD_ZEITPLAN);
  const [schritte, setzeSchritte] = useState<SchrittEntwurf[]>([
    {
      reihenfolge: 0,
      rezeptId: null,
      rezeptFassung: null,
      fortsetzenWenn: "ohne_fehler",
    },
  ]);
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);

  /* Bestand einlesen, sobald er da ist — einmal, nicht bei jedem Tastendruck. */
  useEffect(() => {
    const a = bestand.data;
    if (!a) return;
    setzeProjektId(a.projektId);
    setzeName(a.name);
    setzeAnlass(a.anlass as QaAnlass);
    setzeBedingungen(a.bedingungen ?? leereQaBedingungsgruppe());
    setzeZustand(a.zustand as "entwurf" | "aktiv" | "pausiert");
    setzeBeruhigung(a.beruhigungSekunden);
    setzeBudget(a.budgetMarken);
    setzeZeitplan(a.zeitplan ?? STANDARD_ZEITPLAN);
    setzeSchritte(
      a.schritte.map((s) => ({
        reihenfolge: s.reihenfolge,
        rezeptId: s.rezeptId,
        rezeptFassung: s.rezeptFassung,
        fortsetzenWenn: s.fortsetzenWenn as FortsetzenWenn,
      })),
    );
  }, [bestand.data]);

  const rezeptName = (id: string | null): string =>
    rezepte.data?.find((r) => r.id === id)?.name ?? "Rezept wählen";

  const satz = useMemo(
    () =>
      vorschauSatz(
        anlass,
        schritte.map((s) => ({
          rezeptName: rezeptName(s.rezeptId),
          fortsetzenWenn: s.fortsetzenWenn,
        })),
        bedingungen,
      ),
    [anlass, bedingungen, schritte, rezepte.data],
  );

  const bedingungsMaengel = useMemo(
    () => pruefeQaBedingungen(bedingungen, zielartenFuerQaAnlass(anlass)),
    [anlass, bedingungen],
  );

  const speichern = useMutation({
    mutationFn: async () => {
      const gemeinsam = {
        name: name.trim(),
        anlass,
        zustand,
        beruhigungSekunden: beruhigung,
        budgetMarken: budget,
        bedingungen,
        zeitplan: anlass === "zeitplan_ausgeloest" ? zeitplan : null,
        schritte: schritte.map((s, i) => ({
          reihenfolge: i,
          rezeptId: s.rezeptId,
          rezeptFassung: s.rezeptFassung,
          fortsetzenWenn: s.fortsetzenWenn,
        })),
      };
      if (istNeu) {
        return api.autoQa.automationAnlegen.mutate({ projektId, ...gemeinsam });
      }
      return api.autoQa.automationAendern.mutate({
        id: automationId,
        erwarteteVersion: bestand.data?.version ?? 1,
        ...gemeinsam,
      });
    },
    onSuccess: async (a) => {
      setzeMeldung(null);
      await queryClient.invalidateQueries({ queryKey: ["cubic", "auto-qa"] });
      if (istNeu) {
        void navigate({
          to: "/auto-qa/automationen/$automationId",
          params: { automationId: a.id },
        });
      }
    },
    onError: (fehler) =>
      setzeMeldung(
        meldungZu(grundVon(fehler), {
          netzfehler: istNetzfehler(fehler),
          fallback: "Automation nicht gespeichert",
        }),
      ),
  });

  const loeschen = useMutation({
    mutationFn: () => api.autoQa.automationLoeschen.mutate({ id: automationId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cubic", "auto-qa"] });
      void navigate({ to: "/auto-qa", search: {} });
    },
  });

  function aendereSchritt(i: number, teil: Partial<SchrittEntwurf>): void {
    setzeSchritte((alt) =>
      alt.map((s, j) => (i === j ? { ...s, ...teil } : s)),
    );
  }

  function verschiebe(i: number, richtung: -1 | 1): void {
    const ziel = i + richtung;
    if (ziel < 0 || ziel >= schritte.length) return;
    setzeSchritte((alt) => {
      const neu = [...alt];
      [neu[i], neu[ziel]] = [neu[ziel]!, neu[i]!];
      return neu.map((s, j) => ({ ...s, reihenfolge: j }));
    });
  }

  const kannSpeichern =
    name.trim().length > 0 &&
    (istNeu ? projektId.length > 0 : true) &&
    schritte.length > 0 &&
    schritte.every((s) => s.rezeptId !== null) &&
    bedingungsMaengel.length === 0 &&
    (anlass !== "zeitplan_ausgeloest" ||
      (zeitplan.minute >= 0 &&
        zeitplan.minute <= 59 &&
        zeitplan.stunde >= 0 &&
        zeitplan.stunde <= 23));

  if (!istNeu && bestand.isLoading) {
    return <Mikrolabel grad={12}>Lade Automation …</Mikrolabel>;
  }

  return (
    <div className="max-w-[1040px]">
      <Link
        to="/auto-qa"
        search={{}}
        className={cx(
          "text-[12px] text-akzent underline decoration-1 underline-offset-[3px]",
          focusRing,
        )}
      >
        ← Auto Q&A
      </Link>

      <div className="mt-4 flex items-center gap-3">
        <Zustandsmarke farbe={zustand === "aktiv" ? "blau" : "grau"}>
          {zustand === "aktiv"
            ? "Aktiv"
            : zustand === "pausiert"
              ? "Pausiert"
              : "Entwurf"}
        </Zustandsmarke>
        {!istNeu ? (
          <Mikrolabel grad={11.5}>Fassung {bestand.data?.version}</Mikrolabel>
        ) : null}
      </div>

      <div className="mt-3">
        <Titel>{istNeu ? "Neue Automation" : name || "Automation"}</Titel>
      </div>

      {/* Name und Projekt */}
      <div className="mt-9">
        <Rubrik>Name</Rubrik>
        <div className="mt-4 flex flex-wrap gap-3">
          <Input
            value={name}
            onChange={(e) => setzeName(e.target.value)}
            placeholder="Sicherheit vor Güte"
            className="max-w-[340px]"
          />
          {istNeu ? (
            <Select value={projektId} onValueChange={setzeProjektId}>
              <SelectTrigger aria-label="Projekt" className="w-[240px]">
                <SelectValue placeholder="Projekt wählen …" />
              </SelectTrigger>
              <SelectContent>
                {(projekte.data ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.kuerzel} · {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
      </div>

      {/* D1 — Anlass */}
      <div className="mt-10">
        <Rubrik>Anlass</Rubrik>
        <div className="mt-4">
          <AnlassKatalogAuswahl wert={anlass} onChange={setzeAnlass} />

          <p className="mt-3 max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
            Der Anlass bestimmt, wann Cubic hinsieht. Der Ausdruck darunter
            entscheidet anschließend am konkreten Ziel, ob wirklich ein Auftrag
            entsteht.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Mikrolabel grad={12}>Beruhigungsfrist</Mikrolabel>
            <Input
              type="number"
              min={0}
              max={3600}
              value={beruhigung}
              onChange={(e) => setzeBeruhigung(Number(e.target.value))}
              className="w-[110px]"
            />
            <Mikrolabel grad={11.5}>
              Sekunden — ein zweiter Anlass am selben Ziel schiebt die Frist,
              statt einen zweiten Auftrag zu starten.
            </Mikrolabel>
          </div>

          {anlass === "zeitplan_ausgeloest" ? (
            <div className="mt-5 max-w-[760px] border border-linie bg-flaeche2 p-4">
              <div className="flex flex-wrap items-end gap-3">
                <label className="grid gap-1.5">
                  <Mikrolabel grad={11.5}>Intervall</Mikrolabel>
                  <Select
                    value={zeitplan.intervall}
                    onValueChange={(intervall) =>
                      setzeZeitplan((alt) => ({
                        ...alt,
                        intervall: intervall as QaZeitplan["intervall"],
                      }))
                    }
                  >
                    <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stuendlich">Stündlich</SelectItem>
                      <SelectItem value="taeglich">Täglich</SelectItem>
                      <SelectItem value="woechentlich">Wöchentlich</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                {zeitplan.intervall === "woechentlich" ? (
                  <label className="grid gap-1.5">
                    <Mikrolabel grad={11.5}>Wochentag</Mikrolabel>
                    <Select
                      value={String(zeitplan.wochentag ?? 1)}
                      onValueChange={(wert) =>
                        setzeZeitplan((alt) => ({ ...alt, wochentag: Number(wert) }))
                      }
                    >
                      <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"].map((tag, index) => (
                          <SelectItem key={tag} value={String(index + 1)}>{tag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                ) : null}
                {zeitplan.intervall !== "stuendlich" ? (
                  <label className="grid gap-1.5">
                    <Mikrolabel grad={11.5}>Stunde</Mikrolabel>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={zeitplan.stunde}
                      onChange={(event) =>
                        setzeZeitplan((alt) => ({ ...alt, stunde: Number(event.target.value) }))
                      }
                      className="w-[90px]"
                    />
                  </label>
                ) : null}
                <label className="grid gap-1.5">
                  <Mikrolabel grad={11.5}>Minute</Mikrolabel>
                  <Input
                    type="number"
                    min={0}
                    max={59}
                    value={zeitplan.minute}
                    onChange={(event) =>
                      setzeZeitplan((alt) => ({ ...alt, minute: Number(event.target.value) }))
                    }
                    className="w-[90px]"
                  />
                </label>
                <label className="grid min-w-[220px] flex-1 gap-1.5">
                  <Mikrolabel grad={11.5}>Zeitzone</Mikrolabel>
                  <Input
                    value={zeitplan.zeitzone}
                    onChange={(event) =>
                      setzeZeitplan((alt) => ({ ...alt, zeitzone: event.target.value }))
                    }
                    placeholder="Europe/Berlin"
                  />
                </label>
              </div>
              <p className="mt-3 text-[12px] leading-relaxed text-tinte4">
                Sommerzeit wird über die IANA-Zeitzone berücksichtigt. Nicht existente
                Ortszeiten werden übersprungen; doppelte Zeiten bleiben reale Läufe.
              </p>
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Mikrolabel grad={12}>Markenbudget</Mikrolabel>
            <Input
              type="number"
              min={1}
              value={budget ?? ""}
              placeholder="ohne Grenze"
              onChange={(e) =>
                setzeBudget(e.target.value === "" ? null : Number(e.target.value))
              }
              className="w-[130px]"
            />
            <Mikrolabel grad={11.5}>
              Leer heißt: keine Grenze. Die Vorgabe ist hergeleitet, nicht
              gemessen.
            </Mikrolabel>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <AutoQaBedingungsEditor
          projektId={projektId}
          anlass={anlass}
          wert={bedingungen}
          onChange={setzeBedingungen}
          maengel={bedingungsMaengel}
        />
      </div>

      {/* D3 — Schritte */}
      <div className="mt-10">
        <Rubrik anzahl={`${schritte.length} von ${QA_SCHRITTE_HOECHSTENS}`}>
          Schritte
        </Rubrik>

        {schritte.map((schritt, i) => (
          <div key={i} className="border-b border-linie py-5">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex-none font-mono text-[12px] tabular-nums text-tinte4">
                {String(i + 1).padStart(2, "0")}
              </span>

              <Select
                value={schritt.rezeptId ?? ""}
                onValueChange={(w) => aendereSchritt(i, { rezeptId: w })}
              >
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Rezept wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {(rezepte.data ?? []).map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={
                  schritt.rezeptFassung === null
                    ? "neueste"
                    : String(schritt.rezeptFassung)
                }
                onValueChange={(w) =>
                  aendereSchritt(i, {
                    rezeptFassung: w === "neueste" ? null : Number(w),
                  })
                }
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="neueste">Neueste Fassung</SelectItem>
                  {(
                    rezepte.data?.find((r) => r.id === schritt.rezeptId)
                      ?.fassungen ?? []
                  ).map((f) => (
                    <SelectItem key={f.id} value={String(f.version)}>
                      Fassung {f.version}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => verschiebe(i, -1)}
                  disabled={i === 0}
                  aria-label={`Schritt ${i + 1} nach oben`}
                  className={cx("px-1 text-[13px] text-tinte4 disabled:opacity-30", focusRing)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => verschiebe(i, 1)}
                  disabled={i === schritte.length - 1}
                  aria-label={`Schritt ${i + 1} nach unten`}
                  className={cx("px-1 text-[13px] text-tinte4 disabled:opacity-30", focusRing)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setzeSchritte((alt) =>
                      alt
                        .filter((_, j) => j !== i)
                        .map((s, j) => ({ ...s, reihenfolge: j })),
                    )
                  }
                  disabled={schritte.length === 1}
                  className={cx(
                    "text-[12px] text-akzent underline decoration-1 underline-offset-[3px] disabled:opacity-30",
                    focusRing,
                  )}
                >
                  Entfernen
                </button>
              </div>
            </div>

            {/*
             * Die Fortsetzungsbedingung des ersten Schritts beschreibt, was
             * *nach* ihm gilt — er selbst läuft immer. Deshalb steht sie auch
             * dort, aber mit diesem Hinweis.
             */}
            <div className="mt-4 pl-8">
              <Mikrolabel grad={11.5}>
                {i === 0
                  ? "Der erste Schritt läuft immer — diese Bedingung gilt für den nächsten."
                  : "Dieser Schritt läuft …"}
              </Mikrolabel>
              <RadioCardGroup
                value={schritt.fortsetzenWenn}
                onValueChange={(w) =>
                  aendereSchritt(i, { fortsetzenWenn: w as FortsetzenWenn })
                }
                className="mt-2 sm:grid-cols-2"
              >
                {FORTSETZEN_WENN.map((wenn) => (
                  <RadioCardItem key={wenn} value={wenn}>
                    <div className="flex items-start gap-3">
                      <RadioCardIndicator className="mt-0.5" />
                      <div>
                        <div className="text-[13px] text-tinte">
                          {FORTSETZEN_LABEL[wenn]}
                        </div>
                        <div className="mt-0.5 text-[11.5px] leading-[1.5] text-tinte4">
                          {FORTSETZEN_TEXT[wenn]}
                        </div>
                      </div>
                    </div>
                  </RadioCardItem>
                ))}
              </RadioCardGroup>
            </div>
          </div>
        ))}

        {schritte.length < QA_SCHRITTE_HOECHSTENS ? (
          <button
            type="button"
            onClick={() =>
              setzeSchritte((alt) => [
                ...alt,
                {
                  reihenfolge: alt.length,
                  rezeptId: null,
                  rezeptFassung: null,
                  fortsetzenWenn: "ohne_fehler",
                },
              ])
            }
            className={cx(
              "mt-4 text-[12.5px] text-akzent underline decoration-1 underline-offset-[3px]",
              focusRing,
            )}
          >
            Schritt hinzufügen
          </button>
        ) : null}
      </div>

      {/* Die Vorschau in einem Satz — die wichtigste Prüfung der Maske. */}
      <div className="mt-10 border-t border-tinte pt-5">
        <Mikrolabel grad={12} ton="laut" betont>
          Vorschau
        </Mikrolabel>
        <p className="mt-2.5 max-w-[62ch] text-[15px] font-light leading-[1.7] text-tinte">
          {satz}
        </p>
      </div>

      {/* Zustand */}
      <div className="mt-9">
        <Rubrik>Zustand</Rubrik>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Select
            value={zustand}
            onValueChange={(w) =>
              setzeZustand(w as "entwurf" | "aktiv" | "pausiert")
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="entwurf">Entwurf</SelectItem>
              <SelectItem value="aktiv">Aktiv</SelectItem>
              <SelectItem value="pausiert">Pausiert</SelectItem>
            </SelectContent>
          </Select>
          <Mikrolabel grad={11.5}>
            Eine pausierte Automation sammelt keine Anlässe nach — was während
            der Pause geschieht, ist danach nicht mehr eingeholt.
          </Mikrolabel>
        </div>
      </div>

      {meldung ? (
        <div className="mt-6">
          <Fehlerzeile meldung={meldung} />
        </div>
      ) : null}

      <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-linie pt-5">
        <button
          type="button"
          onClick={() => speichern.mutate()}
          disabled={!kannSpeichern || speichern.isPending}
          className={cx(
            "bg-tinte px-4 py-2 text-[13px] text-papier transition-opacity duration-150 ease-aus disabled:opacity-40",
            focusRing,
          )}
        >
          {speichern.isPending ? "Speichert …" : "Speichern"}
        </button>

        {!istNeu ? (
          <button
            type="button"
            onClick={() => loeschen.mutate()}
            disabled={loeschen.isPending}
            className={cx(
              "text-[12.5px] text-akzent underline decoration-1 underline-offset-[3px] disabled:opacity-40",
              focusRing,
            )}
          >
            Löschen
          </button>
        ) : null}

        {!kannSpeichern ? (
          <Mikrolabel grad={11.5}>
            Name, Projekt, ein Rezept je Schritt und gültige Bedingungen sind
            Pflicht.
          </Mikrolabel>
        ) : null}
      </div>

      {/*
       * D — Probelauf. Er braucht ein bestehendes Ziel, deshalb steht er erst
       * nach dem Speichern zur Verfügung: An einer ungespeicherten Automation
       * gibt es nichts zu erproben.
       */}
      {!istNeu ? <Probelauf automationId={automationId} /> : null}
    </div>
  );
}

/**
 * „An einem Merge Request erproben".
 *
 * Wer eine Automation scharf schaltet, ohne sie einmal gesehen zu haben,
 * schaltet blind. Der Probelauf schreibt Befunde, ändert aber keine Zustände.
 */
function Probelauf({ automationId }: { automationId: string }) {
  const navigate = useNavigate();
  const [zielId, setzeZielId] = useState("");
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);

  const erproben = useMutation({
    mutationFn: () =>
      api.autoQa.erproben.mutate({
        automationId,
        zielArt: "merge_request",
        zielId,
      }),
    onSuccess: (ergebnis) => {
      if (ergebnis.auftragId) {
        void navigate({
          to: "/auto-qa/auftraege/$auftragId",
          params: { auftragId: ergebnis.auftragId },
        });
      } else {
        setzeMeldung(
          meldungZu(null, {
            fallback:
              "Für dieses Ziel läuft bereits ein Auftrag dieser Automation.",
          }),
        );
      }
    },
    onError: (fehler) =>
      setzeMeldung(
        meldungZu(grundVon(fehler), {
          netzfehler: istNetzfehler(fehler),
          fallback: "Probelauf nicht gestartet",
        }),
      ),
  });

  return (
    <div className="mt-10">
      <Rubrik>Erproben</Rubrik>
      <p className="mt-4 max-w-[62ch] text-[13.5px] font-light leading-[1.65] text-tinte3">
        Ein Probelauf schreibt Befunde, ändert aber keine Zustände und schreibt
        keine Kommentare. In der Übersicht ist er an der Marke zu erkennen.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Input
          value={zielId}
          onChange={(e) => setzeZielId(e.target.value)}
          placeholder="Kennung des Merge Requests"
          className="max-w-[340px] font-mono"
        />
        <button
          type="button"
          onClick={() => erproben.mutate()}
          disabled={zielId.length === 0 || erproben.isPending}
          className={cx(
            "text-[12.5px] text-akzent underline decoration-1 underline-offset-[3px] disabled:opacity-40",
            focusRing,
          )}
        >
          {erproben.isPending ? "Startet …" : "An einem Merge Request erproben"}
        </button>
      </div>
      {meldung ? (
        <div className="mt-3">
          <Fehlerzeile meldung={meldung} />
        </div>
      ) : null}
    </div>
  );
}
